import React, { useEffect, useState } from 'react';
import { Smartphone, X, Download } from 'lucide-react';

/**
 * Listens to the browser's `beforeinstallprompt` event and offers
 * an unobtrusive in-app install banner. Respects the user's choice
 * (dismissed -> hide for 14 days via localStorage).
 *
 * Note: Safari (iOS) does not fire `beforeinstallprompt`. For iOS we
 * fall back to a manual hint banner explaining how to add to home screen.
 */
const DISMISS_KEY = 'uh_pwa_install_dismissed_at';
const DISMISS_DAYS = 14;

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function dismissedRecently() {
  try {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    const ts = parseInt(v, 10);
    if (Number.isNaN(ts)) return false;
    const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return ageDays < DISMISS_DAYS;
  } catch {
    return false;
  }
}

export default function InstallPwaPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (dismissedRecently()) return;

    // Android/Chrome path
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Defer rendering by 2 seconds to avoid CLS on first paint
      setTimeout(() => setVisible(true), 2000);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);

    const onAppInstalled = () => {
      setVisible(false);
      setIosHint(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', onAppInstalled);

    // iOS path - no event, just show a soft hint after 6 seconds.
    if (isIos()) {
      const t = setTimeout(() => setIosHint(true), 6000);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
        window.removeEventListener('appinstalled', onAppInstalled);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === 'dismissed') {
        try {
          localStorage.setItem(DISMISS_KEY, String(Date.now()));
        } catch {/* ignore */}
      }
    } catch {/* ignore */}
    setDeferredPrompt(null);
    setVisible(false);
  };

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {/* ignore */}
    setVisible(false);
    setIosHint(false);
  };

  if (visible && deferredPrompt) {
    return (
      <div
        className="fixed z-40 bottom-28 left-3 right-3 sm:left-auto sm:right-4 sm:bottom-4 sm:w-80 rounded-2xl border border-slate-200 bg-white shadow-lg p-4"
        role="dialog"
        aria-live="polite"
        data-testid="pwa-install-banner"
      >
        <button
          type="button"
          aria-label="Schließen"
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 rounded-md text-slate-400 hover:text-slate-700"
          data-testid="pwa-install-dismiss"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-sky-50 text-[hsl(var(--primary))] flex items-center justify-center shrink-0">
            <Smartphone className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="font-heading font-semibold text-[hsl(var(--primary))]">UnfallHeld als App</div>
            <p className="text-sm text-slate-600 mt-1">
              Installieren Sie UnfallHeld auf Ihrem Gerät - schnellerer Start im Notfall, auch offline erreichbar.
            </p>
            <div className="mt-3 flex gap-2">
              <button
                onClick={handleInstall}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[hsl(var(--primary))] text-white text-sm font-medium hover:bg-[hsl(210_73%_12%)] transition-colors"
                data-testid="pwa-install-confirm"
              >
                <Download className="h-4 w-4" /> Installieren
              </button>
              <button
                onClick={handleDismiss}
                className="px-3 py-2 rounded-xl text-sm text-slate-700 hover:bg-slate-100"
                data-testid="pwa-install-later"
              >
                Später
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (iosHint && !visible) {
    return (
      <div
        className="fixed z-40 bottom-28 left-3 right-3 sm:left-auto sm:right-4 sm:bottom-4 sm:w-80 rounded-2xl border border-slate-200 bg-white shadow-lg p-4"
        role="dialog"
        aria-live="polite"
        data-testid="pwa-install-banner-ios"
      >
        <button
          type="button"
          aria-label="Schließen"
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 rounded-md text-slate-400 hover:text-slate-700"
          data-testid="pwa-install-dismiss-ios"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-sky-50 text-[hsl(var(--primary))] flex items-center justify-center shrink-0">
            <Smartphone className="h-5 w-5" />
          </div>
          <div>
            <div className="font-heading font-semibold text-[hsl(var(--primary))]">App auf iPhone hinzufügen</div>
            <p className="text-sm text-slate-600 mt-1">
              Tippen Sie unten auf <strong>Teilen</strong> und dann auf <strong>Zum Home-Bildschirm</strong>, um UnfallHeld als App zu installieren.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
